import React, { useState } from "react";
import {
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Grid,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import {
  Help as HelpIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  CloudDownload as ExportIcon,
  Info as InfoIcon,
  ExpandMore,
} from "@mui/icons-material";
import DraggableHelpDialog from "../../common/components/DraggableHelpDialog";

const TimeFrameHelp = ({ open, onClose }) => {
  const [expandedPanel, setExpandedPanel] = useState("overview");

  const handlePanelChange = (panel) => (event, isExpanded) => {
    setExpandedPanel(isExpanded ? panel : false);
  };

  return (
    <DraggableHelpDialog dgKey="time-frame-help" open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <HelpIcon color="primary" />
          <Typography variant="h6">Time Setup – Time Frame Help Guide</Typography>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        <Box>
          {/* Overview */}
          <Accordion
            expanded={expandedPanel === "overview"}
            onChange={handlePanelChange("overview")}
          >
            <AccordionSummary expandIcon={<ExpandMore />}>
              <Typography variant="h6">System Overview</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Typography paragraph>
                The Time Frame tab in Time Setup allows you to define and manage
                custom time intervals used for scheduling or processing activities
                in the system. Each entry contains a description and a start time.
                You can search, add, edit, export, and delete time frame entries.
              </Typography>
            </AccordionDetails>
          </Accordion>

          {/* Basic Operations & Field Information */}
          <Accordion
            expanded={expandedPanel === "basicOperations"}
            onChange={handlePanelChange("basicOperations")}
          >
            <AccordionSummary expandIcon={<ExpandMore />}>
              <Typography variant="h6">Basic Operations & Field Information</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Grid container spacing={3}>
                {/* Basic Operations */}
                <Grid item xs={12} md={6}>
                  <Paper sx={{ p: 2, height: "100%" }}>
                    <Typography variant="h6" gutterBottom>
                      Basic Operations
                    </Typography>
                    <List dense>
                      <ListItem>
                        <ListItemIcon>
                          <SearchIcon color="primary" />
                        </ListItemIcon>
                        <ListItemText
                          primary="Search"
                          secondary="Use the search bar to quickly find a time frame by description."
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemIcon>
                          <AddIcon color="primary" />
                        </ListItemIcon>
                        <ListItemText
                          primary="Add Entry"
                          secondary="Click the + button to create a new time frame entry."
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemIcon>
                          <EditIcon color="primary" />
                        </ListItemIcon>
                        <ListItemText
                          primary="Edit Entry"
                          secondary="Click the edit icon in the Actions column to modify an existing time frame."
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemIcon>
                          <ExportIcon color="primary" />
                        </ListItemIcon>
                        <ListItemText
                          primary="Export Data"
                          secondary="Click the download icon to export the time frame list."
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemIcon>
                          <DeleteIcon color="error" />
                        </ListItemIcon>
                        <ListItemText
                          primary="Delete Entry"
                          secondary="Click the delete icon to remove a time frame from the list."
                        />
                      </ListItem>
                    </List>
                  </Paper>
                </Grid>

                {/* Field Information */}
                <Grid item xs={12} md={6}>
                  <Paper sx={{ p: 2, height: "100%" }}>
                    <Typography variant="h6" gutterBottom>
                      Field Descriptions
                    </Typography>
                    <List dense>
                      <ListItem>
                        <ListItemIcon>
                          <InfoIcon color="info" />
                        </ListItemIcon>
                        <ListItemText
                          primary="Description"
                          secondary="A label describing the time range, e.g., '12-12' or '6-6'."
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemIcon>
                          <InfoIcon color="info" />
                        </ListItemIcon>
                        <ListItemText
                          primary="Time"
                          secondary="The start time for this time frame, in HH:mm:ss format."
                        />
                      </ListItem>
                    </List>
                  </Paper>
                </Grid>
              </Grid>
            </AccordionDetails>
          </Accordion>

          {/* Tips & Best Practices*/}
          <Accordion
            expanded={expandedPanel === "tips"}
            onChange={handlePanelChange("tips")}
          >
            <AccordionSummary expandIcon={<ExpandMore />}>
              <Typography variant="h6">Tips & Best Practices</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Typography variant="body2" component="div">
                • Use clear and consistent descriptions for easy identification.
                <br />
                • Double-check the time format to ensure accuracy.
                <br />
                • Export regularly to back up your time frame settings.
                <br />
                • Avoid overlapping time frames unless specifically required.
              </Typography>
            </AccordionDetails>
          </Accordion>
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} color="primary">
          Close
        </Button>
      </DialogActions>
    </DraggableHelpDialog>
  );
};

export default TimeFrameHelp;
